import { z } from "zod";
import { PILLARS, type Pillar } from "../../enums/metrics";
import { AI_REFERENCE_METRICS } from "./ai-reference";
import { CONTENT_FITNESS_METRICS } from "./content-fitness";
import { INSTRUMENT_METRICS } from "./instruments";

export type { MetricDef } from "./types";
export type { Pillar };
export { PILLARS };

/**
 * Canonical metric catalog (metrics-v0): the v0 scoring dimensions plus the
 * worker-owned instruments, assembled by family. Seeded into `public.metrics`
 * on backend boot; every metric_key FK resolves against it. Per-dimension
 * checks live alongside in `./checks`.
 */
export const METRIC_CATALOG = [
  ...INSTRUMENT_METRICS,
  ...CONTENT_FITNESS_METRICS,
  ...AI_REFERENCE_METRICS,
] as const;

export type MetricKey = (typeof METRIC_CATALOG)[number]["key"];

export const MetricKeySchema = z.enum(
  METRIC_CATALOG.map((metric) => metric.key) as [MetricKey, ...MetricKey[]],
);

/**
 * Named members for the catalog's own keys, so business code that must single
 * out one metric references it instead of spelling it.
 *
 * `satisfies Record<string, MetricKey>` is what makes this safe: a member that
 * does not resolve against METRIC_CATALOG fails to compile, so this table can
 * never invent a metric or drift from the catalog it names. The catalog stays
 * the single source of the value set — this only gives its members names.
 */
export const METRIC_KEY = {
  CWV: "cwv",
  HUMAN_FIRST_CONTENT_QUALITY: "human_first_content_quality",
  CONTENT_DESIGN_ORIENTATION: "content_design_orientation",
  AI_AGENT_READINESS: "ai_agent_readiness",
  JOURNEY_USABILITY_ACTION: "journey_usability_action",
  BRAND_TRUST_CONSISTENCY: "brand_trust_consistency",
  VOICE_TRUST: "voice_trust",
  INCLUSION: "inclusion",
  SOURCE_CONTROL: "source_control",
  PROMINENCE: "prominence",
  ACCURACY: "accuracy",
  COMPETITOR_LEAKAGE: "competitor_leakage",
} as const satisfies Record<string, MetricKey>;
